import { useEffect, useMemo, useRef } from 'react';
import stripAnsi from 'strip-ansi';
import { getDebugLogPath } from '@wix/cli-astro-definitions';
import { useProjectModel } from '../../../model.js';
import { createAstroLogger } from './createAstroLogger.js';
import type { DevLogStream } from './devLogStream.js';
import { getDevLogStream } from './devLogStream.js';

const FORMAT_REGEXP = /^\d{1,2}:\d{1,2}:\d{1,2}\s*/;

const formatMessage = (msg: string, type = 'info') =>
  `[${new Date().toISOString()}] [cli] [${type}] ${stripAnsi(msg).replace(FORMAT_REGEXP, '')}`;

export const useAstroLogger = ({
  isPrefixed = true,
}: {
  isPrefixed?: boolean;
} = {}) => {
  const {
    model: { projectFolder },
  } = useProjectModel();
  const streamRef = useRef<DevLogStream | undefined>();

  const logger = useMemo(
    () =>
      createAstroLogger({
        isPrefixed,
        onLog: (msg: string) => {
          streamRef.current?.write(formatMessage(msg));
        },
        onError: (msg: string) => {
          streamRef.current?.write(formatMessage(msg, 'error'));
        },
      }),
    [isPrefixed]
  );

  useEffect(() => {
    let shouldCleanStream = false;

    void getDevLogStream({
      filePath: getDebugLogPath(projectFolder),
    }).then((devLogStream) => {
      if (shouldCleanStream) {
        devLogStream.release();
        return;
      }

      streamRef.current = devLogStream;
    });

    return () => {
      shouldCleanStream = true;
      streamRef.current?.release();
    };
  }, [projectFolder]);

  return logger;
};
